import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import Button from 'components/Button/Button';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanOfferData } from 'selectors/getLoanOfferData';
import OutOfState from 'components/OutOfState';
import BackToWebsite from 'components/BackToWebsite';
import { ConversionType, trackConversion } from 'utils/analytics';
import { OfferStatusResult } from 'enums/FlowNextResults';
import { FlowComponentType } from 'routes/types';
import ErrorConsolidating from 'components/DebtConsolidation/ErrorConsolidating/ErrorConsolidating';
import FormContainer from 'components/LoanForm/FormContainer';
import StateContainer from 'components/StateContainer';
import Contact, { ContactEmail } from 'components/Contact';

import { ReactComponent as LoadingSpinner } from 'images/loader.svg';
import { ReactComponent as CheckIcon } from 'images/green-check-rounded.svg';

import styles from './OfferStatus.module.scss';

const renderContent = (status: ApplicationStatusName) => {
  switch (status) {
    case ApplicationStatusName.Rejected: {
      return {
        title: 'That’s a bummer',
        subtitle: `It doesn't look like we can approve you for a loan.`, // TODO: Add "healthcare employee" in front of "loan"
      };
    }

    case ApplicationStatusName.AlreadyApplied: {
      return {
        title: 'That’s a bummer',
        subtitle: 'Looks like you have already applied.',
      };
    }

    case ApplicationStatusName.ManualVerify: {
      return {
        title: 'Thanks, we’re confirming your information!',
        subtitle: 'We’re verifying your information, and will contact you once your account is ready!',
      };
    }

    case ApplicationStatusName.FinalReview: {
      return {
        title: 'Final Review',
        subtitle: (
          <>
            <p>
              As a final step, we are reviewing that everything has been completed successfully. We will contact you
              when it’s time to sign the loan agreement.
            </p>
            <p>After signing, you will receive your funds in about 3-5 business days.</p>
          </>
        ),
      };
    }

    default: {
      return {
        title: 'Please answer a few questions about your offer',
        subtitle: (
          <p className={styles.nursegridSubtitle}>
            Reach out today—<strong>takes just a minute.</strong>
          </p>
        ),
      };
    }
  }
};

const isRejected = (status: ApplicationStatusName | ApplicationStatusName) => status === ApplicationStatusName.Rejected;

const isOutOfState = (status: ApplicationStatusName | ApplicationStatusName) =>
  status === ApplicationStatusName.OutOfState;

const OfferStatus = ({ handleNext, navigationInfo }: FlowComponentType): JSX.Element => {
  const { application, isApplicationStatusUpdating } = useSelector(getApplicationData);
  const appData = application!;
  const offerResult = useSelector(getLoanOfferData).data!;

  const applicationStatus = appData?.status || offerResult?.application_status;

  const handleBackToFinances = () => {
    handleNext(OfferStatusResult.BackToFinancialDashboard);
  };

  useEffect(() => {
    if (applicationStatus) {
      analytics.track(applicationStatus);
      if (applicationStatus === ApplicationStatusName.ManualVerify) {
        trackConversion(ConversionType.LoanManualVerify);
      }
    }
  }, []);

  if (isOutOfState(applicationStatus)) {
    return <OutOfState />;
  }
  if (isRejected(applicationStatus)) {
    return <ErrorConsolidating handleNext={handleNext} navigationInfo={navigationInfo} />;
  }

  const content = renderContent(applicationStatus);

  const allowBackToFinances = applicationStatus !== ApplicationStatusName.AlreadyApplied;
  const isManualReview = applicationStatus === ApplicationStatusName.ManualReview;

  if (isApplicationStatusUpdating) {
    return (
      <div className={styles.loadingContainer}>
        <StateContainer icon={<LoadingSpinner />} title="Loading..." />
      </div>
    );
  }

  return (
    <FormContainer
      title={content.title}
      subtitle={content.subtitle}
      icon={
        applicationStatus === ApplicationStatusName.ManualVerify ? (
          <CheckIcon className={styles.checkIcon} />
        ) : undefined
      }
    >
      {!isManualReview && <p className={styles.questionsTitle}>Questions?</p>}
      <Contact showBackButton={false} email={ContactEmail.OutOfState} />

      {allowBackToFinances && !isManualReview && (
        <div className={styles.whatsNextContainer}>
          <p className={styles.whatsNextTitle}>What's next</p>
          <div className={styles.subtitle}>
            See how else Plannery can help, and keep track of your application status.
          </div>
          <Button className={styles.primaryButton} onClick={handleBackToFinances}>
            See Your Finances
          </Button>
        </div>
      )}
      {!allowBackToFinances && <BackToWebsite />}
    </FormContainer>
  );
};

export default OfferStatus;
